/**
 * EdgeDropZone
 *
 * Component that detects when a panel is dragged to the edge of the screen
 * for a prolonged period (hover). When the drag lingers on the edge,
 * it creates a new tab and moves the panel to it.
 */

import React, { useState, useCallback, useRef, useEffect } from "react";
import { AnimatePresence } from "framer-motion";
import { FaPlus, FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { MotionDiv } from "../arc-sidebar/motion-types";
import { useSplitView } from "./SplitViewProvider";
import logger from "@/utils/logger";

// ============================================================================
// Constants
// ============================================================================

/** Time in ms to hover on edge before creating a new tab */
const EDGE_HOVER_DELAY = 800;

/** Width of the edge detection zone in pixels */
const EDGE_ZONE_WIDTH = 40;

// ============================================================================
// Types
// ============================================================================

interface EdgeDropZoneProps {
  children: React.ReactNode;
}

type EdgePosition = "left" | "right" | null;

// ============================================================================
// Component
// ============================================================================

const EdgeDropZone: React.FC<EdgeDropZoneProps> = ({ children }) => {
  const { movePanelToTab, panels } = useSplitView();
  
  const [isDraggingPanel, setIsDraggingPanel] = useState(false);
  const [activeEdge, setActiveEdge] = useState<EdgePosition>(null);
  const [isHovering, setIsHovering] = useState(false);
  const [progress, setProgress] = useState(0);
  
  const hoverTimerRef = useRef<NodeJS.Timeout | null>(null);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const draggingPanelIdRef = useRef<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    };
  }, []);

  // Listen for panel drag start/end at document level
  useEffect(() => {
    const handleDragStart = (e: DragEvent) => {
      // Check if it's a panel drag by looking at the target element
      // The data isn't available in dragstart, but we can check the target
      const target = e.target as HTMLElement;
      const panelHeader = target.closest("[draggable='true']");
      if (panelHeader) {
        // Get panel ID from the closest panel container
        const panelContainer = panelHeader.closest("[data-panel-id]");
        if (panelContainer) {
          const panelId = panelContainer.getAttribute("data-panel-id");
          draggingPanelIdRef.current = panelId;
          setIsDraggingPanel(true);
        }
      }
    };

    const handleDragEnd = () => {
      setIsDraggingPanel(false);
      setActiveEdge(null);
      setIsHovering(false);
      setProgress(0);
      draggingPanelIdRef.current = null;
      
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = null;
      }
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };

    document.addEventListener("dragstart", handleDragStart);
    document.addEventListener("dragend", handleDragEnd);

    return () => {
      document.removeEventListener("dragstart", handleDragStart);
      document.removeEventListener("dragend", handleDragEnd);
    };
  }, []);

  // Start hover timer when activeEdge changes
  const startHoverTimer = useCallback((edge: EdgePosition) => {
    // Clear existing timers
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    
    if (!edge) {
      setIsHovering(false);
      setProgress(0);
      return;
    }

    setIsHovering(true);
    setProgress(0);

    // Progress animation
    const startTime = Date.now();
    progressIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const newProgress = Math.min((elapsed / EDGE_HOVER_DELAY) * 100, 100);
      setProgress(newProgress);
    }, 16);

    // Trigger action after delay
    hoverTimerRef.current = setTimeout(() => {
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
      setProgress(100);
      
      // Get the panel ID that's being dragged
      // We need to get it from the data transfer during drag
      // For now, we'll use the current focused panel or first panel
      const panelId = draggingPanelIdRef.current || panels[0]?.id;
      
      if (panelId && panels.length > 1) {
        // FIX: Pass "new" to let movePanelToTab handle tab creation internally
        // This avoids a race condition where createTab() changes currentTabId before
        // movePanelToTab can find the panel in the old tab's root
        movePanelToTab(panelId, "new");
        
        logger.debug("[EdgeDropZone] Created new tab from edge drag", {
          edge,
          panelId,
        });
      }
      
      // Reset state
      setIsHovering(false);
      setProgress(0);
      setActiveEdge(null);
    }, EDGE_HOVER_DELAY);
  }, [movePanelToTab, panels]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    if (!containerRef.current) return;
    
    // Check if it's a panel drag
    const hasPanelType = e.dataTransfer.types.includes("application/x-split-panel");
    if (!hasPanelType) return;
    
    // Update dragging state if not already set
    if (!isDraggingPanel) {
      setIsDraggingPanel(true);
    }
    
    e.preventDefault();
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    
    let newEdge: EdgePosition = null;
    
    if (x < EDGE_ZONE_WIDTH) {
      newEdge = "left";
    } else if (x > rect.width - EDGE_ZONE_WIDTH) {
      newEdge = "right";
    }
    
    if (newEdge !== activeEdge) {
      setActiveEdge(newEdge);
      startHoverTimer(newEdge);
    }
  }, [isDraggingPanel, activeEdge, startHoverTimer]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    if (!containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const { clientX, clientY } = e;
    
    // Check if we're actually leaving the container
    if (
      clientX < rect.left ||
      clientX > rect.right ||
      clientY < rect.top ||
      clientY > rect.bottom
    ) {
      setActiveEdge(null);
      startHoverTimer(null);
    }
  }, [startHoverTimer]);

  // Container drop handler - just resets state, actual drops handled by edge zones
  const handleContainerDrop = useCallback(() => {
    // Reset state on any drop
    setActiveEdge(null);
    startHoverTimer(null);
  }, [startHoverTimer]);

  // Only show edge zones when we have multiple panels and are dragging
  const showEdgeZones = isDraggingPanel && panels.length > 1;

  // Handle edge-specific drag events
  const handleEdgeDragOver = useCallback((edge: EdgePosition) => (e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes("application/x-split-panel")) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    if (activeEdge !== edge) {
      setActiveEdge(edge);
      startHoverTimer(edge);
    }
  }, [activeEdge, startHoverTimer]);

  const handleEdgeDragEnter = useCallback((edge: EdgePosition) => (e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes("application/x-split-panel")) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    setActiveEdge(edge);
    startHoverTimer(edge);
  }, [startHoverTimer]);

  const handleEdgeDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Only reset if leaving to outside the edge zones
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (!relatedTarget?.closest("[data-edge-zone]")) {
      setActiveEdge(null);
      startHoverTimer(null);
    }
  }, [startHoverTimer]);

  const handleEdgeDrop = useCallback((e: React.DragEvent) => {
    const hasType = e.dataTransfer.types.includes("application/x-split-panel");
    const dataFromTransfer = e.dataTransfer.getData("application/x-split-panel");
    const dataFromRef = draggingPanelIdRef.current;
    
    if (!hasType) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    const panelId = dataFromTransfer || dataFromRef;
    
    if (panelId && panels.length > 1) {
      // Pass "new" to let movePanelToTab handle tab creation internally
      // This avoids a race condition where createTab() changes currentTabId before
      // movePanelToTab can find the panel in the old tab's root
      movePanelToTab(panelId, "new");
      
      logger.debug("[EdgeDropZone] Created new tab from edge drop", {
        edge: activeEdge,
        panelId,
      });
    }
    
    // Reset state
    setIsDraggingPanel(false);
    setActiveEdge(null);
    setIsHovering(false);
    setProgress(0);
    
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }, [activeEdge, panels.length, movePanelToTab]);

  return (
    <div
      ref={containerRef}
      className="relative h-full w-full"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleContainerDrop}
    >
      {children}

      {/* Edge Drop Zones - these have pointer-events to intercept drags */}
      <AnimatePresence>
        {showEdgeZones && (
          <>
            {/* Left Edge Zone - Interactive */}
            <MotionDiv
              data-edge-zone="left"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
              className="absolute left-0 top-0 bottom-0 z-[60] flex items-center justify-center"
              style={{ width: EDGE_ZONE_WIDTH }}
              onDragOver={handleEdgeDragOver("left")}
              onDragEnter={handleEdgeDragEnter("left")}
              onDragLeave={handleEdgeDragLeave}
              onDrop={handleEdgeDrop}
            >
              <MotionDiv
                animate={{
                  scale: activeEdge === "left" ? 1.1 : 1,
                  backgroundColor: activeEdge === "left" 
                    ? "rgba(var(--arc-accent-rgb, 99, 102, 241), 0.3)" 
                    : "rgba(128, 128, 128, 0.15)",
                }}
                className={`
                  h-full w-full flex items-center justify-center
                  border-r-2 transition-all duration-200 pointer-events-none
                  ${activeEdge === "left" ? "border-accent" : "border-transparent"}
                `}
              >
                <MotionDiv
                  animate={{
                    scale: isHovering && activeEdge === "left" ? 1.2 : 1,
                    opacity: activeEdge === "left" ? 1 : 0.5,
                  }}
                  className={`
                    flex flex-col items-center gap-2 p-3 rounded-lg
                    ${activeEdge === "left" ? "bg-accent/20" : "bg-surface-tertiary/50"}
                  `}
                >
                  <FaArrowLeft 
                    size={16} 
                    className={activeEdge === "left" ? "text-accent" : "text-content-tertiary"} 
                  />
                  <FaPlus 
                    size={12} 
                    className={activeEdge === "left" ? "text-accent" : "text-content-tertiary"} 
                  />
                  
                  {/* Progress indicator */}
                  {isHovering && activeEdge === "left" && (
                    <div className="w-8 h-1 bg-surface-tertiary rounded-full overflow-hidden">
                      <MotionDiv
                        initial={{ width: 0 }}
                        animate={{ width: `${progress}%` }}
                        className="h-full bg-accent rounded-full"
                      />
                    </div>
                  )}
                </MotionDiv>
              </MotionDiv>
            </MotionDiv>

            {/* Right Edge Zone - Interactive */}
            <MotionDiv
              data-edge-zone="right"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.2 }}
              className="absolute right-0 top-0 bottom-0 z-[60] flex items-center justify-center"
              style={{ width: EDGE_ZONE_WIDTH }}
              onDragOver={handleEdgeDragOver("right")}
              onDragEnter={handleEdgeDragEnter("right")}
              onDragLeave={handleEdgeDragLeave}
              onDrop={handleEdgeDrop}
            >
              <MotionDiv
                animate={{
                  scale: activeEdge === "right" ? 1.1 : 1,
                  backgroundColor: activeEdge === "right" 
                    ? "rgba(var(--arc-accent-rgb, 99, 102, 241), 0.3)" 
                    : "rgba(128, 128, 128, 0.15)",
                }}
                className={`
                  h-full w-full flex items-center justify-center
                  border-l-2 transition-all duration-200 pointer-events-none
                  ${activeEdge === "right" ? "border-accent" : "border-transparent"}
                `}
              >
                <MotionDiv
                  animate={{
                    scale: isHovering && activeEdge === "right" ? 1.2 : 1,
                    opacity: activeEdge === "right" ? 1 : 0.5,
                  }}
                  className={`
                    flex flex-col items-center gap-2 p-3 rounded-lg
                    ${activeEdge === "right" ? "bg-accent/20" : "bg-surface-tertiary/50"}
                  `}
                >
                  <FaArrowRight 
                    size={16} 
                    className={activeEdge === "right" ? "text-accent" : "text-content-tertiary"} 
                  />
                  <FaPlus 
                    size={12} 
                    className={activeEdge === "right" ? "text-accent" : "text-content-tertiary"} 
                  />
                  
                  {/* Progress indicator */}
                  {isHovering && activeEdge === "right" && (
                    <div className="w-8 h-1 bg-surface-tertiary rounded-full overflow-hidden">
                      <MotionDiv
                        initial={{ width: 0 }}
                        animate={{ width: `${progress}%` }}
                        className="h-full bg-accent rounded-full"
                      />
                    </div>
                  )}
                </MotionDiv>
              </MotionDiv>
            </MotionDiv>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

EdgeDropZone.displayName = "EdgeDropZone";

export default EdgeDropZone;
